import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, Loader2Icon, PaperclipIcon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';
import { CONTACT_EMAIL } from '../../data/site';
import { useAdminData } from '../../contexts/AdminDataContext';
import { quoteFileName } from '../../utils/quotePdf';
import type { Quote } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function defaultMessage(quote: Quote): string {
  return `Hej ${quote.customerName || 'der'}

Tak for din henvendelse.

Vedhæftet finder du tilbuddet på den aftalte opgave.

Du er meget velkommen til at kontakte mig, hvis du har spørgsmål til tilbuddet.

Med venlig hilsen
LYKKEVÆRK
${CONTACT_EMAIL}`;
}
interface Props {
  quote: Quote;
  onClose: () => void;
  onSent: (email: string) => void;
}

/** Mailkomposer: adminen kan rette modtager og besked, før tilbuddet sendes. */
export function QuoteEmailDialog({
  quote,
  onClose,
  onSent
}: Props) {
  const {
    sendQuoteByEmail,
    updateCustomer
  } = useAdminData();
  const [to, setTo] = useState(quote.customerEmail);
  const [subject, setSubject] = useState(`Tilbud fra LYKKEVÆRK – ${quote.title}`);
  const [message, setMessage] = useState(defaultMessage(quote));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(quote.customerEmail ? '' : 'Kunden har ingen e-mailadresse. Tilføj en e-mail til kunden før tilbuddet kan sendes.');
  async function handleSend() {
    if (sending) return;
    const address = to.trim();
    if (!address) {
      setError('Kunden har ingen e-mailadresse. Tilføj en e-mail til kunden før tilbuddet kan sendes.');
      return;
    }
    if (!emailPattern.test(address)) {
      setError('E-mailadressen er ikke gyldig. Ret adressen, og prøv igen.');
      return;
    }
    setSending(true);
    setError('');

    // Rettet adresse gemmes også på kunden, så den er der næste gang.
    if (quote.customerId && address !== quote.customerEmail) {
      await updateCustomer(quote.customerId, {
        email: address
      }).catch(() => null);
    }
    const result = await sendQuoteByEmail(quote, {
      to: address,
      subject,
      message
    });
    setSending(false);
    if (!result.ok) {
      setError(result.error ?? 'Tilbuddet kunne ikke sendes. Prøv igen.');
      return;
    }
    onSent(address);
  }
  return <div className="fixed inset-0 z-50 overflow-y-auto bg-ink/40 px-4 py-8" role="dialog" aria-modal="true" aria-labelledby="send-tilbud-titel" data-id="element-73" data-mp="1hn1gxqpncwbp">
      <motion.div initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.2,
      ease
    }} className="mx-auto w-full max-w-xl rounded-card bg-white p-6 md:p-8" data-id="element-74" data-mp="18luyhc3ftbft">
        <div className="flex items-start justify-between gap-4" data-id="element-75" data-mp="3tb0vs9iekav0">
          <h2 id="send-tilbud-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-76" data-mp="3ocpkrvgdfub7">
            Send tilbud på mail
          </h2>
          <button type="button" onClick={onClose} aria-label="Luk" disabled={sending} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink disabled:opacity-50" data-id="element-77" data-mp="3j96x87hm8nmm">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-78" data-mp="v7e18tnm1zn" />
          </button>
        </div>

        <div className="mt-6 space-y-5" data-id="element-79" data-mp="5spgqhh2vkz7">
          <TextField id="mail-til" label="Til" type="email" required value={to} onChange={event => {
          setTo(event.target.value);
          setError('');
        }} data-id="element-80" data-mp="atwq2kyvcs7z" />

          <div className="rounded-xl bg-canvas px-4 py-3 text-sm text-ink-soft" data-id="element-81" data-mp="2cn32erj9xod3">
            <p data-id="element-82" data-mp="2hkmmw96kexdh">
              <span className="text-ink-muted" data-id="element-83" data-mp="2hm7atfydji2d">Fra:</span> {CONTACT_EMAIL}
            </p>
            <p className="mt-1.5 flex items-center gap-2" data-id="element-84" data-mp="btzkkeo1v0ia">
              <PaperclipIcon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" data-id="element-85" data-mp="6xitpcpgu81i" />
              {quoteFileName(quote.quoteNumber)}
            </p>
          </div>

          <TextField id="mail-emne" label="Emne" value={subject} onChange={event => setSubject(event.target.value)} data-id="element-86" data-mp="fxnrcv4uxs2h" />

          <TextArea id="mail-besked" label="Besked" rows={11} value={message} onChange={event => setMessage(event.target.value)} data-id="element-87" data-mp="3oauzx98dym0s" />
        </div>

        {error && <p role="alert" className="mt-5 flex items-start gap-2 text-sm text-danger" data-id="element-88" data-mp="3tc2839efxn7z">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-89" data-mp="35m7wsw9rs6pu" />
            {error}
          </p>}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-90" data-mp="30mvb4eumx5ou">
          <Button size="lg" onClick={handleSend} disabled={sending} data-id="element-91" data-mp="1g52kb7sol9ps">
            {sending ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-92" data-mp="1kxcjleform4q" />
                Sender …
              </> : 'Send tilbud'}
          </Button>
          <Button variant="secondary" size="lg" onClick={onClose} disabled={sending} data-id="element-93" data-mp="1c528zw0vpemy">
            Annuller
          </Button>
        </div>
      </motion.div>
    </div>;
}